/**
 * The Book's draft (slice 8b): what the player has set for the pending call, before Snap. It is
 * nothing but a decision in waiting: `toDecision` turns it into the same `OffenseDecision` /
 * `DefenseDecision` the AI hands the engine, and `preview` shows the personnel through the sim's
 * own `applyWindow`, so the board, the spends and the field read what the engine will play. Fresh
 * subs carry `enteredThisPlay`; their stages clear. Nothing here decides an outcome.
 */
import {
  DEFAULT_PLAYBOOK,
  DEFENSE_SLOTS,
  OFFENSE_SLOTS,
  applyWindow,
  balance,
  charted,
  healMove,
  hasWindow,
  itemOf,
  leanedSlots,
  lineupOf,
  playById,
  restRefusal,
  slotKind,
  type BagUse,
  type Combatant,
  type ConversionChoice,
  type DefenseDecision,
  type DefensePlayDef,
  type DefenseSlot,
  type FourthDownChoice,
  type Game,
  type MoveEffect,
  type OffenseDecision,
  type OffensePlayDef,
  type OffenseSlot,
  type Playbook,
  type RestRefusal,
  type Status,
  type Sub,
  type SubWindow,
  type Team,
  type TeamId,
  type Tempo,
  type TypeName,
} from '@tdmons/sim';
import { moveLine } from './moveLine';

export type Side = 'offense' | 'defense';
export type Slot = OffenseSlot | DefenseSlot;

/**
 * A drafted sub. `swap` (slice 8c, client only) marks a MOVE: the Pokémon `in` is already on the
 * field, at the `swap` slot, and trades places with the one at `slot` (the sim's swap path in
 * `applyWindow`). `toDecision` sends `{ slot, in }` only.
 */
export interface DraftSub extends Sub<Slot> {
  swap?: Slot;
}

/** What the player has set in the Book for the pending call. Nothing is sent until Snap. */
export interface Draft {
  team: TeamId;
  side: Side;
  /**
   * The player's play for this call, `null` until they pick one (slice 11c: every call starts
   * with none, so the AI's default never stands in for the player's choice).
   */
  playId: string | null;
  /** Bench to slot (or a swap), in the order chosen; `applyWindow` applies them in this order. */
  subs: DraftSub[];
  /** One spend per slot at most, on any slot that fights a battle in the play (slice 8d). */
  moves: Partial<Record<Slot, string>>;
  /** Bench roster indices that spend Rest. */
  rest: number[];
  bag: BagUse[];
  /** The offense's 4th-down call, from the text box's choice. */
  fourthDown?: FourthDownChoice;
  /** The offense's try, from the text box's choice. */
  conversion?: ConversionChoice;
}

const slotsOf = (side: Side): readonly Slot[] =>
  side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
const playsOf = (side: Side, book: Playbook): readonly (OffensePlayDef | DefensePlayDef)[] =>
  side === 'offense' ? book.offense : book.defense;

export function newDraft(team: TeamId, side: Side, playId: string | null = null): Draft {
  return { team, side, playId, subs: [], moves: {}, rest: [], bag: [] };
}

/** Why the Book's Matchups, Moves and Bag tabs wait (slice 11c). */
export const PICK_A_PLAY = 'Pick a play first';
/** The Book's footer before a play is picked. */
export const NO_PLAY = 'No play yet';

/**
 * Whether the draft has its play yet; until then the Book's other tabs are locked with
 * `PICK_A_PLAY`, since the spendable slots, the battles and the board's lines all come from it.
 */
export const hasPlay = (d: Draft): d is Draft & { playId: string } => d.playId !== null;

/** The draft's sub window (empty fields left out). */
export function windowOf(d: Draft): SubWindow<Slot> {
  return {
    ...(d.subs.length ? { subs: d.subs.map((s) => ({ slot: s.slot, in: s.in })) } : {}),
    ...(d.rest.length ? { rest: [...d.rest] } : {}),
    ...(d.bag.length ? { bag: d.bag.map((u) => ({ ...u })) } : {}),
  };
}

/**
 * The decision Snap (or Hurry) sends. Every slot of the side is in `moves`, `null` unless chosen,
 * so no play's default script fires behind the player's back. Hurry-up has no sub window.
 */
export function toDecision(d: Draft, tempo: Tempo = 'normal'): OffenseDecision | DefenseDecision {
  // A backstop: the action row and the Book never send a draft without a play (rowAction).
  if (d.playId === null) throw new Error('no play picked: the Book opens on Plays first');
  const hurry = tempo === 'hurry-up';
  // Hurry-up drops the window, MOVEs too: a spend drafted on a swapped slot was for the Pokémon
  // the swap would have put there, so it goes with it.
  const swapped = hurry ? swapsOf(d) : new Map<Slot, Slot>();
  const moves = Object.fromEntries(
    slotsOf(d.side).map((s) => [s, swapped.has(s) ? null : (d.moves[s] ?? null)]),
  );
  const window = hurry ? {} : windowOf(d);
  if (d.side === 'defense') {
    return { playId: d.playId, moves, ...window } as DefenseDecision;
  }
  return {
    playId: d.playId,
    moves,
    ...window,
    ...(tempo === 'hurry-up' ? { tempo } : {}),
    ...(d.fourthDown ? { fourthDown: d.fourthDown } : {}),
    ...(d.conversion ? { conversion: d.conversion } : {}),
  } as OffenseDecision;
}

/** The game with the player's window applied: the personnel the engine will play. */
export function preview(game: Readonly<Game>, d: Draft): Game {
  const w = windowOf(d);
  if (!hasWindow(w)) return game as Game;
  const { team } = applyWindow(game.teams[d.team], d.side, w);
  return { ...game, teams: { ...game.teams, [d.team]: team } };
}

/**
 * The slots a move can be spent on in this play: every slot that fights a battle (slice 8d). The
 * sim spends a move wherever it is sent; the play's leaned-on slots are what it hinges on (the KEY
 * tag), not the only ones who fight. The AI still spends only there (sim ai/spend.ts).
 */
export function spendableSlots(side: Side, playId: string, book: Playbook = DEFAULT_PLAYBOOK) {
  const play = playById(playsOf(side, book), playId);
  return slotsOf(side).filter((s) => slotKind(play, s, balance)?.battles);
}

export function withPlay(d: Draft, playId: string, book: Playbook = DEFAULT_PLAYBOOK): Draft {
  const keep = new Set<Slot>(spendableSlots(d.side, playId, book));
  const moves = Object.fromEntries(
    Object.entries(d.moves).filter(([s]) => keep.has(s as Slot)),
  ) as Draft['moves'];
  return { ...d, playId, moves };
}

/** Send `index` in at `slot` (replacing a sub drafted there); a fresh sub spends nothing. */
export function withSub(d: Draft, slot: Slot, index: number): Draft {
  const at = d.subs.findIndex((s) => s.slot === slot && !s.swap);
  const subs =
    at < 0
      ? [...d.subs, { slot, in: index }]
      : d.subs.map((s, i) => (i === at ? { slot, in: index } : s));
  const moves = { ...d.moves };
  delete moves[slot];
  return { ...d, subs, moves, rest: d.rest.filter((i) => i !== index) };
}

/** The side's chart before the window. */
const chartOf = (game: Readonly<Game>, d: Draft): Record<string, number> => {
  const team = game.teams[d.team];
  return { ...(d.side === 'offense' ? team.offense : team.defense) };
};

/** One drafted entry applied to a chart, as `applyWindow` does: a swap trades two slots. */
function applyTo(chart: Record<string, number>, s: DraftSub): void {
  const at = Object.keys(chart).find((k) => chart[k] === s.in);
  if (at !== undefined) chart[at] = chart[s.slot] as number;
  chart[s.slot] = s.in;
}

/**
 * Take back the sub at `slot`. A later sub that sent in the Pokémon this one took out would now
 * move a starter between slots, not bring one off the bench, so it goes too. Drafted swaps
 * (MOVE) stay, re-aimed at whoever is in their partner slot by then.
 */
export function withoutSub(game: Readonly<Game>, d: Draft, slot: Slot): Draft {
  return replay(
    game,
    d,
    d.subs.filter((s) => s.swap !== undefined || s.slot !== slot),
  );
}

/** Rebuild the drafted entries on the chart in order, dropping subs that would now be swaps. */
function replay(game: Readonly<Game>, d: Draft, entries: readonly DraftSub[]): Draft {
  const chart = chartOf(game, d);
  const subs: DraftSub[] = [];
  for (const s of entries) {
    if (s.swap !== undefined) {
      const partner = chart[s.swap] as number;
      const swap = { slot: s.slot, in: partner, swap: s.swap };
      applyTo(chart, swap);
      subs.push(swap);
      continue;
    }
    if (Object.values(chart).includes(s.in)) continue;
    applyTo(chart, s);
    subs.push(s);
  }
  return settleRest(game, { ...d, subs });
}

/**
 * MOVE (slice 8c): the Pokémon at `slot` and the one at `other` trade places, through the sim's
 * swap path (a sub whose Pokémon is already in the chart; stages kept, no "fresh sub" rule). Any
 * spend drafted on either slot is dropped: it was for the Pokémon that was there.
 */
export function withSwap(game: Readonly<Game>, d: Draft, slot: Slot, other: Slot): Draft {
  const chart = chartOf(game, d);
  for (const s of d.subs) applyTo(chart, s);
  const moves = { ...d.moves };
  delete moves[slot];
  delete moves[other];
  return { ...d, subs: [...d.subs, { slot, in: chart[other] as number, swap: other }], moves };
}

/** Undo the MOVE `slot` is part of (either end), and any spend drafted since on the two slots. */
export function withoutSwap(game: Readonly<Game>, d: Draft, slot: Slot): Draft {
  const hit = d.subs.find((s) => s.swap !== undefined && (s.slot === slot || s.swap === slot));
  if (!hit) return d;
  const moves = { ...d.moves };
  delete moves[hit.slot];
  delete moves[hit.swap as Slot];
  return {
    ...replay(
      game,
      d,
      d.subs.filter((s) => s !== hit),
    ),
    moves,
  };
}

/** Each slot in a drafted MOVE, with its partner. */
export function swapsOf(d: Draft): Map<Slot, Slot> {
  const out = new Map<Slot, Slot>();
  for (const s of d.subs) {
    if (s.swap === undefined) continue;
    out.set(s.slot, s.swap);
    out.set(s.swap, s.slot);
  }
  return out;
}

export function withMove(d: Draft, slot: Slot, moveId: string | null): Draft {
  const moves = { ...d.moves };
  if (moveId === null) delete moves[slot];
  else moves[slot] = moveId;
  return { ...d, moves };
}

export const withBag = (d: Draft, use: BagUse): Draft => ({ ...d, bag: [...d.bag, use] });
export const withoutBag = (d: Draft, i: number): Draft => ({
  ...d,
  bag: d.bag.filter((_, k) => k !== i),
});
export const withRest = (d: Draft, index: number): Draft =>
  d.rest.includes(index) ? d : { ...d, rest: [...d.rest, index] };
export const withoutRest = (d: Draft, index: number): Draft => ({
  ...d,
  rest: d.rest.filter((i) => i !== index),
});

// ---------------------------------------------------------------------------------------------
// What the Book shows, read off the preview.

/** Why a slot's whole block of moves is greyed; the play's reason comes first (slice 8c, 8d). */
export type SlotBlock =
  | 'the pass is a read, not a battle'
  | 'no battle on this play'
  | 'no move on the first snap'
  | '0 HP';
export type TileBlock = 'no PP' | 'no football effect yet';

export interface MoveTile {
  moveId: string;
  name: string;
  type: TypeName;
  /** PP left this game, of `max` (the divide-by-5 PP): "PP 2/2" is full, as in the games. */
  left: number;
  max: number;
  /** A short label for the football effect. */
  effect: string;
  /** The football translation under the tile (slice 8d, game/moveLine.ts). */
  line: string;
  disabled?: TileBlock;
}

export interface SpendSlot {
  slot: Slot;
  index: number;
  name: string;
  types: readonly TypeName[];
  /** KEY TO THIS PLAY: a slot the play leans on that can spend this snap (never a greyed one). */
  key: boolean;
  blocked?: SlotBlock;
  tiles: MoveTile[];
}

/** The effects the engine spends at the snap (play.ts `spendMoves`); the rest land later. */
const SPENT_AT_SNAP: ReadonlySet<MoveEffect['kind']> = new Set(['attack', 'burst', 'stage']);
const STAT_LABEL = { atk: 'ATK', def: 'DEF', spa: 'SPA', spd: 'SPD', spe: 'SPE' } as const;

/** The short label on a move tile: "POW 90", "+2 SPE", "+1 ATK". */
export function effectLabel(e: MoveEffect): string {
  switch (e.kind) {
    case 'attack':
      return `POW ${e.power}`;
    case 'burst':
      return `+${e.speedStages} SPE`;
    case 'stage':
      return `${e.stages > 0 ? '+' : ''}${e.stages} ${STAT_LABEL[e.stat]}`;
    default:
      return e.kind.toUpperCase();
  }
}

/**
 * The move tiles for every Pokémon on the field for this play, as the engine will treat a spend
 * there (slices 8c, 8d): anyone with a battle can spend; the slots the play leans on come first
 * (KEY), then the rest. A slot with no battle spends nothing: the passer ("the pass is a read, not
 * a battle") or the hand-off ("no battle on this play"); a leaned-on one keeps its place but loses
 * the KEY tag. A fresh sub or a 0-HP Pokémon spends nothing; a move with no PP left, or whose effect
 * the engine does not spend at the snap yet, is skipped. The Book disables exactly those.
 */
export function spendSlots(
  game: Readonly<Game>,
  d: Draft,
  book: Playbook = DEFAULT_PLAYBOOK,
): SpendSlot[] {
  if (d.playId === null) return []; // the Moves tab waits for the play (slice 11c)
  const team = game.teams[d.team];
  const chart = (d.side === 'offense' ? team.offense : team.defense) as Record<Slot, number>;
  const lineup = lineupOf(team, chart);
  const play = playById(playsOf(d.side, book), d.playId);
  const leaned = new Set<Slot>(leanedSlots(play));
  const live = new Set<Slot>(spendableSlots(d.side, d.playId, book));
  const all = slotsOf(d.side);
  const ordered = [
    ...all.filter((s) => leaned.has(s) && live.has(s)),
    ...all.filter((s) => leaned.has(s) && !live.has(s)),
    ...all.filter((s) => !leaned.has(s) && live.has(s)),
    ...all.filter((s) => !leaned.has(s) && !live.has(s)),
  ];
  return ordered.map((slot) => {
    const c = lineup[slot] as Combatant;
    const blocked: SlotBlock | undefined = !live.has(slot)
      ? slotKind(play, slot, balance)?.kind === 'throw'
        ? 'the pass is a read, not a battle'
        : 'no battle on this play'
      : balance.subs.noMoveOnFirstSnap && c.enteredThisPlay
        ? 'no move on the first snap'
        : c.hp <= 0
          ? '0 HP'
          : undefined;
    const tiles = c.moves.map((m): MoveTile => {
      const left = c.ppLeft[m.id] ?? 0;
      const disabled: TileBlock | undefined =
        left <= 0
          ? 'no PP'
          : !SPENT_AT_SNAP.has(m.effect.kind)
            ? 'no football effect yet'
            : undefined;
      return {
        moveId: m.id,
        name: m.name,
        type: m.type,
        left,
        max: m.pp,
        effect: effectLabel(m.effect),
        line: moveLine(m.effect),
        ...(disabled ? { disabled } : {}),
      };
    });
    return {
      slot,
      index: chart[slot],
      name: c.name,
      types: c.types,
      // A greyed slot never shows the tag (a fresh sub or 0 HP on a leaned-on slot included).
      key: leaned.has(slot) && !blocked,
      ...(blocked ? { blocked } : {}),
      tiles,
    };
  });
}

/** Whether a drafted spend would be used: the slot is live and the tile is not disabled. */
export function canSpend(slots: readonly SpendSlot[], slot: Slot, moveId: string): boolean {
  const s = slots.find((x) => x.slot === slot);
  return !!s && !s.blocked && !!s.tiles.find((t) => t.moveId === moveId && !t.disabled);
}

export interface MonView {
  index: number;
  name: string;
  types: readonly TypeName[];
  hp: number;
  maxHp: number;
  status?: Status;
  onField: boolean;
}

const monView = (team: Team, index: number): MonView => {
  const c = team.roster[index] as Combatant;
  return {
    index,
    name: c.name,
    types: c.types,
    hp: c.hp,
    maxHp: c.stats.hp,
    ...(c.status ? { status: c.status.kind } : {}),
    onField: charted(team).has(index),
  };
};

export interface BagItem {
  item: string;
  /** "Super Potion" (balance.json `items`). */
  name: string;
  count: number;
  kind: 'heal-hp' | 'cure-status';
  /** HP an `heal-hp` item restores. */
  hp?: number;
}

export const itemName = (id: string): string =>
  (balance.items as unknown as Record<string, { name?: string } | undefined>)[id]?.name ?? id;

/** The bag as the preview leaves it: each item left, with how many. */
export function bagItems(game: Readonly<Game>, d: Draft): BagItem[] {
  const counts = new Map<string, number>();
  for (const id of game.teams[d.team].bag ?? []) counts.set(id, (counts.get(id) ?? 0) + 1);
  return [...counts].flatMap(([item, count]): BagItem[] => {
    const e = itemOf(item)?.effect;
    const name = itemName(item);
    if (e?.kind === 'heal-hp') return [{ item, name, count, kind: 'heal-hp', hp: e.hp }];
    if (e?.kind === 'cure-status') return [{ item, name, count, kind: 'cure-status' }];
    return [];
  });
}

export interface BagTarget extends MonView {
  /** Using the item here would do nothing. */
  disabled?: 'full HP' | 'no status';
}

/** Everyone on the team, field first, as a target for `item` (the preview's HP and status). */
export function bagTargets(game: Readonly<Game>, d: Draft, item: BagItem): BagTarget[] {
  const team = game.teams[d.team];
  return team.roster
    .map((_, i) => monView(team, i))
    .sort((x, y) => Number(y.onField) - Number(x.onField))
    .map((m) => {
      const disabled =
        item.kind === 'heal-hp' && m.hp >= m.maxHp
          ? ('full HP' as const)
          : item.kind === 'cure-status' && !m.status
            ? ('no status' as const)
            : undefined;
      return { ...m, ...(disabled ? { disabled } : {}) };
    });
}

export interface RestOption extends MonView {
  moveName: string;
  sitPlays: number;
  drafted: boolean;
  /** Why the sim would refuse this Rest (`restRefusal`); the Book disables it. */
  disabled?: Exclude<RestRefusal, 'on the field' | 'no heal move'>;
  /** Plays left sitting out an earlier Rest, when `disabled` is 'sitting'. */
  sitLeft?: number;
}

/**
 * Every Pokémon in neither chart after the drafted subs that knows a heal move (Rest, Recover),
 * with the sim's own `restRefusal` as the reason it is disabled (0 HP, sitting out an earlier
 * Rest, no PP). A Pokémon in a chart is never offered: Rest is spent on the bench.
 */
export function restOptions(game: Readonly<Game>, d: Draft): RestOption[] {
  const before = preview(game, { ...d, rest: [], bag: [] });
  const team = before.teams[d.team];
  const on = charted(team);
  return team.roster.flatMap((c, i) => {
    const refused = restRefusal(team, i, on);
    const move = healMove(c);
    if (refused === 'on the field' || refused === 'no heal move') return [];
    if (move?.effect.kind !== 'heal') return [];
    return [
      {
        ...monView(team, i),
        moveName: move.name,
        sitPlays: move.effect.sitPlays,
        drafted: !refused && d.rest.includes(i),
        ...(refused ? { disabled: refused } : {}),
        ...(refused === 'sitting' ? { sitLeft: c.sitPlays ?? 0 } : {}),
      },
    ];
  });
}

/**
 * The drafted Rests the sim would still take after the drafted subs. Taking back a sub can put a
 * one-way starter who was drafted to Rest back in a chart, where Rest would throw at the snap.
 */
function settleRest(game: Readonly<Game>, d: Draft): Draft {
  if (!d.rest.length) return d;
  const team = preview(game, { ...d, rest: [], bag: [] }).teams[d.team];
  const on = charted(team);
  const rest = d.rest.filter((i) => restRefusal(team, i, on) === null);
  return rest.length === d.rest.length ? d : { ...d, rest };
}

/**
 * "Quick Slants · 2 subs · 1 swap · 1 move · 1 item": the play, then what Snap sends beyond it
 * ("Quick Slants" alone with nothing else); "No play yet" until there is one (slice 11c).
 */
export function draftSummary(d: Draft, book: Playbook = DEFAULT_PLAYBOOK): string {
  if (d.playId === null) return NO_PLAY;
  const play = playById(playsOf(d.side, book), d.playId).name;
  const extras = extrasSummary(d);
  return extras ? `${play} · ${extras}` : play;
}

function extrasSummary(d: Draft): string {
  const n = (k: number, one: string, many: string) => (k === 1 ? `1 ${one}` : `${k} ${many}`);
  const subs = d.subs.filter((s) => s.swap === undefined).length;
  const swaps = d.subs.length - subs;
  const parts = [
    subs ? n(subs, 'sub', 'subs') : '',
    swaps ? n(swaps, 'swap', 'swaps') : '',
    Object.keys(d.moves).length ? n(Object.keys(d.moves).length, 'move', 'moves') : '',
    d.rest.length ? n(d.rest.length, 'Rest', 'Rests') : '',
    d.bag.length ? n(d.bag.length, 'item', 'items') : '',
  ].filter(Boolean);
  return parts.join(' · ');
}
